import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.tsx';
import { useApp } from '../state/app.tsx';
import { toastMs } from '../lib/ux-prefs.ts';

/**
 * 提示条。
 *
 * ════════════════════════════════════════════════════════
 * 一条产品原则：**能撤销的操作就不弹「确定要这样吗」。**
 * ════════════════════════════════════════════════════════
 * 先做了，再给一个后悔的机会。打断比确认框少一次。
 * 思路来自 sonner（emilkowalski/sonner，MIT，13k★）。
 *
 * 反过来，**不能撤销的操作照样要确认**（删钱包会连成交历史一起删，
 * 那份历史是期望值和连亏统计的输入，删了算不回来）。
 * 区别只在「撤销这件事做不做得到」，不在「操作看起来危险不危险」。
 */

export type ToastKind = 'info' | 'ok' | 'warn' | 'bad';

export interface ToastOptions {
  kind?: ToastKind;
  /** 停留时长。带撤销时默认更久 —— 用户要有时间反应过来 */
  ms?: number;
  /** 撤销回调。传了就多一个「撤销」按钮 */
  undo?: (() => void) | undefined;
}

interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
  undo?: (() => void) | undefined;
}

export interface ToastApi {
  show: (text: string, opts?: ToastOptions) => void;
  /** 快捷方式：出错了 */
  fail: (text: string) => void;
}

const Ctx = createContext<ToastApi>({
  show: () => undefined,
  fail: () => undefined
});

export const useToast = (): ToastApi => useContext(Ctx);


export function ToastHost({ children }: { children: React.ReactNode }): React.ReactElement {
  const [items, setItems] = useState<ToastItem[]>([]);
  /* 停留多久跟通用设置的 ux_toastSec 走（带撤销的多停 3 秒，见 lib/ux-prefs.ts） */
  const settings = useApp().settings;
  const seq = useRef(0);
  /* 定时器要能被撤销时提前清掉，否则点了撤销条子还挂在那儿 */
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const drop = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setItems(list => list.filter(x => x.id !== id));
  }, []);

  const show = useCallback((text: string, opts: ToastOptions = {}) => {
    /* 空文案不弹。宁可什么都不显示，也不要弹一个空条子让人以为界面坏了 */
    if (text.trim().length === 0) return;
    const id = ++seq.current;
    const item: ToastItem = {
      id,
      text,
      kind: opts.kind ?? 'info',
      ...(opts.undo ? { undo: opts.undo } : {})
    };
    setItems(list => {
      /* 最多同时四条。再多就把最老的挤掉 ——
         一屏堆满提示条会把界面本身遮住 */
      const next = [...list, item];
      return next.length > 4 ? next.slice(next.length - 4) : next;
    });
    const ms = opts.ms ?? toastMs(settings, opts.undo !== undefined);
    timers.current.set(id, setTimeout(() => drop(id), ms));
  }, [drop, settings]);

  const fail = useCallback((text: string) => show(text, { kind: 'bad', ms: 6000 }), [show]);

  const api = useMemo<ToastApi>(() => ({ show, fail }), [show, fail]);

  /* 挂到 body 上那个 #toasts 里。在 React 树里渲染会被 .app 的
     overflow 和层叠上下文裁掉 —— 那个坑在原型里踩过 */
  const host = typeof document === 'undefined' ? null : document.getElementById('toasts');

  return (
    <Ctx.Provider value={api}>
      {children}
      {host
        ? createPortal(
            items.map(t => (
              <div className={'toast ' + t.kind} key={t.id} role="status">
                <Icon name={t.kind === 'bad' ? 'alert' : t.kind === 'ok' ? 'done' : 'info'} cls="sm" />
                <span className="grow">{t.text}</span>
                {t.undo ? (
                  <button
                    type="button"
                    className="toast-undo"
                    onClick={() => {
                      t.undo?.();
                      drop(t.id);
                    }}
                  >
                    <Icon name="undo" cls="sm" />
                    撤销
                  </button>
                ) : null}
                <button type="button" className="cbtn" aria-label="关闭" onClick={() => drop(t.id)}>
                  <Icon name="x" cls="sm" />
                </button>
              </div>
            )),
            host
          )
        : null}
    </Ctx.Provider>
  );
}
